import React, { useState, useMemo } from 'react';
import Header from './components/Header.js';
import EventCard from './components/EventCard.js';
import EventFilters from './components/EventFilters.js';
import EventDetails from './components/EventDetails.js';
import RegistrationModal from './components/RegistrationModal.js';
import Dashboard from './components/Dashboard.js';
import { mockEvents } from './data/mockData.js';
import './styles/main.css';

function App() {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeView, setActiveView] = useState('events');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [showEventDetails, setShowEventDetails] = useState(false);
  const [registrations, setRegistrations] = useState([]);
  
  const [filters, setFilters] = useState({
    category: 'All',
    priceRange: { min: 0, max: Infinity },
    dateRange: { start: '', end: '' }
  });

  // Filter and search events
  const filteredEvents = useMemo(() => {
    return mockEvents.filter(event => {
      // Search filter
      const matchesSearch = event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          event.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          event.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
      
      // Category filter
      const matchesCategory = filters.category === 'All' || event.category === filters.category;
      
      // Price filter
      const matchesPrice = event.price >= filters.priceRange.min && event.price <= filters.priceRange.max;
      
      // Date filter
      let matchesDate = true;
      if (filters.dateRange.start) {
        matchesDate = matchesDate && event.date >= filters.dateRange.start;
      }
      if (filters.dateRange.end) {
        matchesDate = matchesDate && event.date <= filters.dateRange.end;
      }
      
      return matchesSearch && matchesCategory && matchesPrice && matchesDate;
    });
  }, [searchTerm, filters]);

  // Calculate event counts for filters
  const eventCounts = useMemo(() => {
    const counts = { All: mockEvents.length };
    mockEvents.forEach(event => {
      counts[event.category] = (counts[event.category] || 0) + 1;
    });
    return counts;
  }, []);

  const handleRegister = (event, formData = null) => {
    if (formData) {
      // Create registration
      const newRegistration = {
        id: `reg_${Date.now()}`,
        eventId: event.id,
        userId: 'user1',
        registrationDate: new Date().toISOString().split('T')[0],
        status: 'confirmed',
        ticketNumber: `${event.category.toUpperCase()}${new Date().getFullYear()}-${String(Math.floor(Math.random() * 100000)).padStart(6, '0')}`,
        userName: formData.name,
        userEmail: formData.email
      };
      
      setRegistrations(prev => [...prev, newRegistration]);
      
      // Show success message (you could implement a toast notification here)
      alert(`Successfully registered for ${event.title}! Your ticket number is ${newRegistration.ticketNumber}`);
    } else {
      setSelectedEvent(event);
      setShowRegistrationModal(true);
    }
  };

  const handleViewDetails = (event) => {
    setSelectedEvent(event);
    setShowEventDetails(true);
  };

  return React.createElement('div', { className: 'app' },
    React.createElement(Header, {
      searchTerm: searchTerm,
      setSearchTerm: setSearchTerm,
      activeView: activeView,
      setActiveView: setActiveView
    }),
    
    React.createElement('main', { className: 'main-content' },
      React.createElement('div', { className: 'container' },
        activeView === 'events' ? 
          React.createElement('div', { className: 'content-layout' },
            React.createElement(EventFilters, {
              filters: filters,
              setFilters: setFilters,
              eventCounts: eventCounts
            }),
            
            React.createElement('div', { className: 'events-section' },
              React.createElement('div', { className: 'events-header' },
                React.createElement('h2', null, 'Discover Amazing Events'),
                React.createElement('span', { className: 'events-count' },
                  `${filteredEvents.length} event${filteredEvents.length !== 1 ? 's' : ''} found`
                )
              ),
              
              React.createElement('div', { className: 'events-grid' },
                filteredEvents.map(event =>
                  React.createElement(EventCard, {
                    key: event.id,
                    event: event,
                    onRegister: handleRegister,
                    onViewDetails: handleViewDetails
                  })
                )
              ),
              
              filteredEvents.length === 0 &&
                React.createElement('div', { className: 'empty-state' },
                  React.createElement('h3', null, 'No events found'),
                  React.createElement('p', null, 'Try adjusting your search criteria or filters.')
                )
            )
          ) :
          React.createElement(Dashboard, { events: mockEvents })
      )
    ),
    
    // Modals
    showRegistrationModal && selectedEvent &&
      React.createElement(RegistrationModal, {
        event: selectedEvent,
        onClose: () => {
          setShowRegistrationModal(false);
          setSelectedEvent(null);
        },
        onRegister: handleRegister
      }),
    
    showEventDetails && selectedEvent &&
      React.createElement(EventDetails, {
        event: selectedEvent,
        onClose: () => {
          setShowEventDetails(false);
          setSelectedEvent(null);
        },
        onRegister: handleRegister
      })
  );
}

export default App;